import React, { useMemo, useState } from "react";
import { Table, message } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getSupplierColumns } from "./getSupplierColumns";
import CustomModal from "../../../components/CustomModal/CustomModal";
import ReasonForm from "../../../components/CustomForms/ReasonForm";
import EditSupplierModal from "./EditSupplierModal";
import { updateSupplierStatus } from "../../../redux/slices/supplierSlice";
import { TABLE_PAGINATION } from "../../../constants/tableConfig";

const SupplierTable = ({
  data = [],
  tableData = [],
  loading = false,
  searchText = "",
  selectedStatus = "ALL",
  onRefresh,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { statusLoading } = useSelector((state) => state.supplier);

  const [modal, setModal] = useState({ type: null, data: null });
  const [editingSupplier, setEditingSupplier] = useState(null);

  const filteredData = useMemo(() => {
    const rawList = data.length > 0 ? data : (tableData || []);
    if (!searchText) return rawList;
    const query = searchText.toLowerCase();
    return rawList.filter(
      (item) =>
        item.supplierName?.toLowerCase().includes(query) ||
        item.supplierId?.toLowerCase().includes(query) ||
        item.pocName?.toLowerCase().includes(query) ||
        item.pocEmail?.toLowerCase().includes(query) ||
        item.pocMobile?.toLowerCase().includes(query) ||
        item.bankName?.toLowerCase().includes(query) ||
        item.status?.toLowerCase().includes(query)
    );
  }, [data, tableData, searchText]);

  const handleStatusSubmit = async (values) => {
    if (!modal.type || !modal.data?.supplierId) return;

    const reason = typeof values === "string" ? values : (values?.reason || values?.remarks || "");
    const nextStatus = modal.type === "deactivate" ? "INACTIVE" : "ACTIVE";
    const res = await dispatch(
      updateSupplierStatus({
        supplierId: modal.data.supplierId,
        status: nextStatus,
        remarks: reason,
        reason: reason,
      })
    );

    if (
      res?.meta?.requestStatus === "fulfilled" &&
      (res?.payload?.statusCode === 200 ||
        res?.payload?.data?.msg ||
        res?.payload?.msg)
    ) {
      message.success(
        `Supplier "${modal.data.supplierName}" ${
          nextStatus === "ACTIVE" ? "activated" : "deactivated"
        } successfully.`
      );
      setModal({ type: null, data: null });
      onRefresh?.();
    } else {
      const errMsg =
        res?.payload?.response?.data?.message ||
        res?.payload?.message ||
        res?.payload ||
        "Failed to update supplier status.";
      message.error(typeof errMsg === "string" ? errMsg : JSON.stringify(errMsg));
    }
  };

  const columns = useMemo(
    () =>
      getSupplierColumns({
        onView: (id, status) =>
          navigate(`/suppliers/supplier-details/${id}/${status || selectedStatus}`),
        onEdit: (supplier) => setEditingSupplier(supplier),
        onDeactivate: (supplier) => setModal({ type: "deactivate", data: supplier }),
        onActivate: (supplier) => setModal({ type: "activate", data: supplier }),
      }),
    [selectedStatus, navigate]
  );

  return (
    <>
      <Table
        rowKey="supplierId"
        columns={columns}
        dataSource={filteredData}
        loading={loading}
        pagination={TABLE_PAGINATION}
        className="supplier-table"
        scroll={{ x: 1000 }}
      />

      {modal.type && (
        <CustomModal
          title={modal.type === "deactivate" ? `Deactivate ${modal.data?.supplierName || "Supplier"}` : `Activate ${modal.data?.supplierName || "Supplier"}`}
          open={Boolean(modal.type)}
          centered
          width={500}
          onCancel={() => setModal({ type: null, data: null })}
          content={
            <ReasonForm
              label={modal.type === "deactivate" ? "Reason for Deactivation" : "Reason / Remarks"}
              placeholder={
                modal.type === "deactivate"
                  ? "Enter the reason for deactivating this supplier..."
                  : "Enter remarks (optional)..."
              }
              btnName={modal.type === "deactivate" ? "Deactivate" : "Activate"}
              btnType={modal.type === "deactivate" ? "danger" : "primary"}
              submitLoading={statusLoading}
              onClose={() => setModal({ type: null, data: null })}
              handleOnFormSubmit={handleStatusSubmit}
            />
          }
        />
      )}

      {editingSupplier && (
        <EditSupplierModal
          open={Boolean(editingSupplier)}
          supplier={editingSupplier}
          onClose={() => setEditingSupplier(null)}
          onSuccess={() => {
            onRefresh?.();
            setEditingSupplier(null);
          }}
        />
      )}
    </>
  );
};

export default SupplierTable;
